import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { stateToUrl, type SearchState } from '@wineryxplorer/shared';

interface SavedSearchesMenuProps {
  currentState: SearchState;
  currentVenueCount: number;
  defaultSaveName: string;
}

export const SavedSearchesMenu = ({
  currentState,
  currentVenueCount,
  defaultSaveName,
}: SavedSearchesMenuProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(defaultSaveName);
  const containerRef = useRef<HTMLDivElement>(null);
  const utils = trpc.useUtils();

  // Keep the input prefilled with the current default name until the user
  // edits it. Once the dropdown opens we stop syncing — otherwise the user's
  // typing would get clobbered if `defaultSaveName` recomputes upstream.
  useEffect(() => {
    if (!isOpen) setName(defaultSaveName);
  }, [defaultSaveName, isOpen]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Escape closes the dropdown — WCAG keyboard-dismissal pattern for popovers.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Only fetch when authenticated AND open — saves a request for anon users
  // and avoids preloading list before the user opens the menu.
  const { data: savedList } = trpc.savedSearch.list.useQuery(undefined, {
    enabled: isAuthenticated && isOpen,
  });

  const saveMutation = trpc.savedSearch.save.useMutation({
    onSuccess: () => {
      utils.savedSearch.list.invalidate();
      setName(defaultSaveName); // reset so subsequent saves start fresh
    },
  });
  const deleteMutation = trpc.savedSearch.delete.useMutation({
    onSuccess: () => utils.savedSearch.list.invalidate(),
  });

  const handleSave = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    saveMutation.mutate({
      name: trimmed,
      searchState: currentState,
      venueCountAtSave: currentVenueCount,
    });
  }, [name, currentState, currentVenueCount, saveMutation]);

  const handleLoad = useCallback(
    (state: SearchState) => {
      navigate(stateToUrl(state));
      setIsOpen(false);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate({ id });
    },
    [deleteMutation]
  );

  return (
    <div ref={containerRef} className="relative">
      {/* Native `title` has a 500–1500ms browser delay we can't shorten — use
          a CSS group-hover tooltip for instant feedback. aria-label carries
          the same text for screen readers, so accessibility is preserved. */}
      <div className="group relative">
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          disabled={!isAuthenticated}
          className="p-2 text-gray-600 hover:text-primary-700 disabled:text-gray-300 disabled:cursor-not-allowed"
          aria-label={isAuthenticated ? 'Saved searches' : 'Sign in to save searches'}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
            />
          </svg>
        </button>
        <span
          role="tooltip"
          className="pointer-events-none absolute right-0 top-full z-40 mt-1 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-75 group-hover:opacity-100"
        >
          {isAuthenticated ? 'Saved searches' : 'Sign in to save searches'}
        </span>
      </div>

      {isOpen && isAuthenticated && (
        <div className="absolute right-0 mt-2 w-80 bg-gray-50 border border-gray-200 rounded-lg shadow-lg z-30">
          <div className="p-3 border-b border-gray-100">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Save this search
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSave();
                  }
                }}
                placeholder="Name"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-info-500"
                autoFocus
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={!name.trim() || saveMutation.isPending}
                className="px-3 py-1 text-sm font-medium text-white bg-primary-700 rounded hover:bg-primary-800 disabled:bg-gray-400"
              >
                Save
              </button>
            </div>
            {saveMutation.error && (
              <p className="text-xs text-red-600 mt-1">{saveMutation.error.message}</p>
            )}
          </div>

          <ul className="max-h-72 overflow-auto">
            {savedList?.length === 0 && (
              <li className="px-3 py-4 text-sm text-gray-500 text-center">
                No saved searches yet.
              </li>
            )}
            {savedList?.map((item) => {
              const label =
                item.searchState.region ??
                item.searchState.city ??
                (item.searchState.bounds ? 'Map area' : 'Search');
              return (
                <li
                  key={item.id}
                  className="flex items-center justify-between px-3 py-2 hover:bg-primary-50 group"
                >
                  <button
                    type="button"
                    onClick={() => handleLoad(item.searchState as SearchState)}
                    className="flex-1 text-left"
                  >
                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                    <div className="text-xs text-gray-500">
                      {label} · {item.venueCountAtSave} winer
                      {item.venueCountAtSave === 1 ? 'y' : 'ies'}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    disabled={deleteMutation.isPending}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 ml-2 disabled:opacity-50"
                    aria-label={`Delete ${item.name}`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22"
                      />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
